<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import { push } from 'svelte-spa-router';

	import { Button, Text } from '@Shelly/shelly-design-system';

	import { user_data, user_settings } from 'src/stores/account/user';
	import { camera_cloud_storage } from 'src/stores/features/cameras/cloud-storage';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { devices } from 'src/stores/lists/devices';
	import { homes } from 'src/stores/account/home';
	import {
		CAMERA_CLOUD_STORAGE_SCROLL_PARAM,
		getSlotCountsFromSettings,
		hasCameraCloudStorage,
		hydrateFromUserSettings,
		isHighestCameraCloudStorageTier,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import {
		CAMERA_CLOUD_STORAGE_PROMO_BANNER_ID,
		CAMERA_CLOUD_STORAGE_PROMO_SURFACE,
		getCameraCloudStoragePromoSnoozeMs,
	} from 'src/helpers/banner_views/banner-ids';
	import {
		dismissBanner,
		isBannerVisible,
		markBannerSeen,
	} from 'src/helpers/banner_views/banner-views-helpers';
	import { CameraCloudStorageSubscriptionStatus } from 'src/types/CameraCloudStorage';
	import type { Device } from 'src/types/Device';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import { isRepoUser } from 'src/helpers/user_server_conditions';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { shellyId16 } from 'src/helpers/interface_helpers';
	import { getCameraDevices } from 'src/components/Camera/helpers/camera-helpers';
	import FeaturePromoBanner from 'src/components/FeaturePromoBanner.svelte';
	import ManageCameraCloudStorageModal from './ManageCameraCloudStorageModal.svelte';

	export let addMarginTop = false;
	/** Device ID of the camera currently shown in the side panel / drawer. */
	export let deviceId: string | undefined;
	/** Read-only output: whether the banner renders anything. Read it with `bind:visible`. */
	export let visible = false;

	let showManageModal = false;
	let showPromo = isBannerVisible(
		CAMERA_CLOUD_STORAGE_PROMO_BANNER_ID,
		CAMERA_CLOUD_STORAGE_PROMO_SURFACE,
	);
	/**
	 * `variant` is derived from `camera_cloud_storage`, which only holds the
	 * account's slots after `hydrateFromUserSettings`. Until then every camera
	 * looks unassigned, so impressions must wait for hydration.
	 */
	let hydrated = false;
	/** Impression key already counted; see `impressionKey`. */
	let countedImpressionKey: string | undefined;

	onMount(() => {
		hydrateFromUserSettings($user_settings);
		showPromo = isBannerVisible(
			CAMERA_CLOUD_STORAGE_PROMO_BANNER_ID,
			CAMERA_CLOUD_STORAGE_PROMO_SURFACE,
		);
		hydrated = true;
	});

	$: isPremium = ($user_data?.premium ?? 0) > 0;
	$: isSharedHome = isActiveHomeShared($homes.list);
	$: activePlan = $camera_cloud_storage.activePlan;
	$: hasActiveCcs = !!activePlan && activePlan.status === CameraCloudStorageSubscriptionStatus.Active;
	$: slotAssignments = $camera_cloud_storage.slotAssignments;
	$: totalSlots = getSlotCountsFromSettings($user_settings).all;
	$: usedSlots = Object.values(slotAssignments ?? {}).filter(Boolean).length;
	$: hasFreeSlots = usedSlots < totalSlots && totalSlots > 0;
	$: isDeviceAssigned = slotAssignments && hasCameraCloudStorage(deviceId ?? '');
	$: cameraDeviceCount = getCameraDevices($devices ?? {}).length;
	$: currentDevice = (
		deviceId ? ($devices?.[deviceId] ?? $devices?.[shellyId16(deviceId)]) : undefined
	) as Device | undefined;
	$: canAssignThisDevice = !currentDevice?.shared;

	type BannerVariant = 'get-premium' | 'assign' | 'subscribe-ccs' | 'upgrade-ccs' | 'manage' | 'none';

	/**
	 * Variant chain:
	 * 1. Device IS assigned → 'none' (already has a slot; no CTA)
	 * 2. Device NOT assigned + free slots + owned by this account → 'assign'
	 *    (shared cameras cannot occupy a slot here)
	 * 3. Device NOT assigned + Premium + CCS + no free slots + max CCS tier → 'manage'
	 *    (steal a slot from another camera; that camera then becomes unassigned)
	 * 4. Otherwise upsell based on subscription state:
	 *    a. No Premium, No CCS, 1 camera  → 'get-premium'
	 *    a. No Premium, No CCS, 2+ cameras → 'subscribe-ccs'
	 *    b. Premium, No CCS   → 'subscribe-ccs'
	 *    c. No Premium, CCS   → 'get-premium' (devices ≤ tier) or 'upgrade-ccs' (devices > tier, not max)
	 *    d. Premium + CCS      → 'upgrade-ccs' (not max tier)
	 */
	function isMaxedPremiumAndCcs(
		_isPremium: boolean,
		_hasActiveCcs: boolean,
		_hasFreeSlots: boolean,
	): boolean {
		return _isPremium && _hasActiveCcs && !_hasFreeSlots;
	}

	function getUpsellVariant(
		_isPremium: boolean,
		_hasActiveCcs: boolean,
		_cameraDeviceCount: number,
	): BannerVariant {
		if (!_hasActiveCcs) {
			if (_isPremium) return 'subscribe-ccs';
			return _cameraDeviceCount <= 1 ? 'get-premium' : 'subscribe-ccs';
		}
		const tier = activePlan?.tier ?? 0;
		if (!_isPremium && _cameraDeviceCount <= tier) return 'get-premium';
		if (!isHighestCameraCloudStorageTier(tier)) return 'upgrade-ccs';
		return _isPremium ? 'none' : 'get-premium';
	}

	function determineVariant(
		_isPremium: boolean,
		_hasActiveCcs: boolean,
		_hasFreeSlots: boolean,
		_isDeviceAssigned: boolean,
		_cameraDeviceCount: number,
		_deviceId: string | undefined,
		_canAssignThisDevice: boolean,
	): BannerVariant {
		if (_isDeviceAssigned) return 'none';
		if (_deviceId && _hasFreeSlots) {
			return _canAssignThisDevice ? 'assign' : 'none';
		}
		if (
			_canAssignThisDevice &&
			isMaxedPremiumAndCcs(_isPremium, _hasActiveCcs, _hasFreeSlots) &&
			isHighestCameraCloudStorageTier(activePlan?.tier)
		) {
			return 'manage';
		}
		return getUpsellVariant(_isPremium, _hasActiveCcs, _cameraDeviceCount);
	}

	$: variant = determineVariant(
		isPremium,
		hasActiveCcs,
		hasFreeSlots,
		isDeviceAssigned,
		cameraDeviceCount,
		deviceId,
		canAssignThisDevice,
	);

	$: canPromote = !isSharedHome && showPromo;
	$: visible = canPromote && variant !== 'none';

	/**
	 * Identifies what is on screen right now. Counts and impression once
	 */
	$: impressionKey = visible ? `${deviceId ?? 'no-device'}:${variant}` : undefined;
	$: if (hydrated && impressionKey && impressionKey !== countedImpressionKey) {
		countedImpressionKey = impressionKey;
		void markBannerSeen(CAMERA_CLOUD_STORAGE_PROMO_BANNER_ID, {
			scope: 'shared',
			surface: CAMERA_CLOUD_STORAGE_PROMO_SURFACE,
			// `extras` is merged, so this is the variant of the latest impression.
			extras: { variant },
		});
	}

	const navigateToSubscriptions = () => {
		push('/settings/premium');
		resetDrawerClose();
	};

	const navigateToCameraPlans = () => {
		push(`/settings/premium?${CAMERA_CLOUD_STORAGE_SCROLL_PARAM}=1`);
		resetDrawerClose();
	};

	const handleClick = () => {
		switch (variant) {
			case 'assign':
			case 'manage':
				if (deviceId) {
					showManageModal = true;
				} else {
					navigateToSubscriptions();
				}
				break;
			case 'get-premium':
				navigateToSubscriptions();
				break;
			case 'subscribe-ccs':
			case 'upgrade-ccs':
				navigateToCameraPlans();
				break;
		}
	};

	const handleDismiss = () => {
		showPromo = false;
		void dismissBanner(CAMERA_CLOUD_STORAGE_PROMO_BANNER_ID, {
			scope: 'shared',
			surface: CAMERA_CLOUD_STORAGE_PROMO_SURFACE,
			snoozeMs: getCameraCloudStoragePromoSnoozeMs(isRepoUser()),
			dismissSource: 'user',
		});
	};

	function getButtonLabel(_variant: Exclude<BannerVariant, 'none'>): string {
		switch (_variant) {
			case 'get-premium':
				return String($format('button_promo_get_premium', { default: 'Get Premium' }));
			case 'assign':
				return String(
					$format('button_promo_activate_cloud_storage', {
						default: 'Activate Cloud Storage',
					}),
				);
			case 'manage':
				return String(
					$format('modal_title_manage_camera_cloud_storage', {
						default: 'Manage Camera Cloud Storage',
					}),
				);
			case 'subscribe-ccs':
				return String($format('button_promo_subscribe_now', { default: 'Subscribe now' }));
			case 'upgrade-ccs':
				return String($format('button_subscription_upgrade', { default: 'Upgrade' }));
		}
	}
</script>

{#if canPromote && variant !== 'none'}
	<FeaturePromoBanner
		tone={variant === 'get-premium' ? 'premium' : 'camera'}
		icon={variant === 'get-premium' ? 'fas fa-gem' : 'fas fa-camera'}
		class={$showNewLayout || addMarginTop ? 'mt-m' : ''}
		dismissable
		on:dismiss={handleDismiss}
	>
		<Text styleType="body-small" class="feature-promo-banner__text">
			{#if variant === 'get-premium'}
				{$format('promo_banner_get_premium_text', {
					default:
						'Upgrade to Premium and unlock cloud storage for one Shelly Camera, plus a range of exclusive benefits.',
				})}
			{:else if variant === 'assign'}
				{$format('promo_banner_assign_camera_text', {
					default:
						'You have available cloud storage slots. Assign your camera to start using cloud recording.',
				})}
			{:else if variant === 'manage'}
				{$format('promo_banner_manage_camera_text', {
					default:
						'Your Camera Cloud Storage slots are full. Manage assignments to replace a camera or free a slot.',
				})}
			{:else if variant === 'subscribe-ccs'}
				{$format('promo_banner_subscribe_ccs_text', {
					default:
						'Upgrade to Camera Cloud Storage and unlock 30 days of secure video history, plus a range of exclusive benefits.',
				})}
			{:else if variant === 'upgrade-ccs'}
				{$format('promo_banner_upgrade_ccs_text', {
					default:
						'Upgrade your Camera Cloud Storage plan to unlock additional slots for your cameras.',
				})}
			{/if}
		</Text>

		<Button
			slot="action"
			class="feature-promo-banner__button"
			size="m"
			variant={variant === 'get-premium' ? 'premium' : 'secondary'}
			width="full"
			on:click={handleClick}
		>
			{getButtonLabel(variant)}
		</Button>
	</FeaturePromoBanner>
{/if}

{#if showManageModal && !isSharedHome}
	<ManageCameraCloudStorageModal
		bind:showModal={showManageModal}
		{activePlan}
		preselectedCameraId={variant === 'assign' ? deviceId : undefined}
		on:close={() => (showManageModal = false)}
		on:upgrade={() => {
			showManageModal = false;
			navigateToCameraPlans();
		}}
	/>
{/if}
